import styled from 'styled-components';

import { FormField } from '../../../components/forms/FormField';
import { SelectField } from '../../../components/forms/SelectField';
import { Button } from '../../../components/ui/Button';
import type { ComplianceDocumentStatus } from '../../../types/api';
import type { ComplianceFiltersState } from '../lib/filters';

const FiltersContainer = styled.section`
  background: var(--surface);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  padding: var(--space-lg);
  margin-bottom: var(--space-xl);
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: var(--space-md);
  align-items: end;
`;

const FiltersActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: var(--space-md);
`;

const statusOptions: Array<{
  value: ComplianceDocumentStatus | '';
  label: string;
}> = [
  { value: '', label: 'Todos los estados' },
  { value: 'draft', label: 'Borrador' },
  { value: 'active', label: 'Activo' },
  { value: 'expired', label: 'Vencido' },
  { value: 'archived', label: 'Archivado' },
];

const expiryOptions = [
  { value: '', label: 'Todos' },
  { value: '30', label: 'Próximos 30 días' },
  { value: '60', label: 'Próximos 60 días' },
  { value: '90', label: 'Próximos 90 días' },
];

const sortByOptions = [
  { value: 'expiry_date', label: 'Vencimiento' },
  { value: 'created_at', label: 'Creación' },
];

const sortDirOptions = [
  { value: 'asc', label: 'Ascendente' },
  { value: 'desc', label: 'Descendente' },
];

const perPageOptions = [
  { value: '10', label: '10 por página' },
  { value: '20', label: '20 por página' },
  { value: '50', label: '50 por página' },
];

interface ComplianceFiltersProps {
  filters: ComplianceFiltersState;
  typeOptions: Array<{ value: string; label: string }>;
  onFiltersChange: (updates: Partial<ComplianceFiltersState>) => void;
  onClearFilters: () => void;
  showClear: boolean;
}

export const ComplianceFilters = ({
  filters,
  typeOptions,
  onFiltersChange,
  onClearFilters,
  showClear,
}: ComplianceFiltersProps) => {
  return (
    <FiltersContainer aria-label="Filtros de cumplimiento">
      <FiltersGrid>
        <FormField
          label="Buscar"
          name="compliance-search"
          placeholder="Tipo, autoridad, jurisdicción o notas"
          value={filters.search}
          onChange={(event) => onFiltersChange({ search: event.target.value })}
        />

        <SelectField
          label="Estado"
          name="compliance-status"
          options={statusOptions}
          value={filters.status}
          onChange={(value) =>
            onFiltersChange({
              status: (value ?? '') as ComplianceFiltersState['status'],
            })
          }
        />

        <SelectField
          label="Vencimiento"
          name="compliance-expiry-window"
          options={expiryOptions}
          value={filters.expiryWindow}
          onChange={(value) =>
            onFiltersChange({
              expiryWindow: (value ??
                '') as ComplianceFiltersState['expiryWindow'],
            })
          }
        />
      </FiltersGrid>
      <details style={{ marginTop: '1rem' }}>
        <summary style={{ cursor: 'pointer', padding: '0.5rem 0' }}>
          Más filtros y orden
        </summary>
        <FiltersGrid>
          <SelectField
            label="Tipo"
            name="compliance-type"
            options={[{ value: '', label: 'Todos los tipos' }, ...typeOptions]}
            value={filters.type}
            onChange={(value) =>
              onFiltersChange({ type: (value ?? '') as string })
            }
            disabled={typeOptions.length === 0}
          />

          <FormField
            label="Etiquetas"
            name="compliance-tags"
            placeholder="ej: habilitacion, higiene"
            value={filters.tags}
            onChange={(event) => onFiltersChange({ tags: event.target.value })}
          />

          <SelectField
            label="Ordenar por"
            name="compliance-sort-by"
            options={sortByOptions}
            value={filters.sortBy}
            onChange={(value) =>
              onFiltersChange({
                sortBy: (value ??
                  'expiry_date') as ComplianceFiltersState['sortBy'],
              })
            }
          />

          <SelectField
            label="Dirección"
            name="compliance-sort-dir"
            options={sortDirOptions}
            value={filters.sortDir}
            onChange={(value) =>
              onFiltersChange({
                sortDir: (value ?? 'asc') as ComplianceFiltersState['sortDir'],
              })
            }
          />

          <SelectField
            label="Resultados"
            name="compliance-per-page"
            options={perPageOptions}
            value={String(filters.perPage)}
            onChange={(value) => {
              const parsed = Number(value);
              onFiltersChange({
                perPage: Number.isNaN(parsed) ? filters.perPage : parsed,
              });
            }}
          />
        </FiltersGrid>
      </details>

      {showClear && (
        <FiltersActions>
          <Button variant="tertiary" size="sm" onClick={onClearFilters}>
            Limpiar filtros
          </Button>
        </FiltersActions>
      )}
    </FiltersContainer>
  );
};

ComplianceFilters.displayName = 'ComplianceFilters';
